import type { IChannel } from "containers/Channels/types";
import type { ChannelParams, GroupParams, ITermFormData } from "containers/TermFormContainer";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import GroupSection from "../GroupSection";
import InactiveItemsCollapse from "../InactiveItemsCollapse";
import type { GroupWithChannels } from "../types";

interface GroupsListSectionProps {
    mode: "new" | "edit";
    activeGroups: GroupWithChannels[];
    inactiveGroups: GroupWithChannels[];
    activedChannels?: { channelId: number }[];
    chosenChannels: ChannelParams[];
    chosenGroups: GroupParams[];
    activeGroupsIds: number[];
    setChosenGroups: Dispatch<SetStateAction<GroupParams[]>>;
    usesTemplate?: boolean;
    onChange?: VoidFunction;
    handleChannelUpdates: (newChannel: unknown, groupId?: number) => void;
    handleToggleFormatTypeOverride: (channelId: number, formatTypeId: number, enabled: boolean) => void;
    handleGroupCalculationBaseColumnChange: (
        groupId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
        applyToEveryFormula?: boolean,
    ) => void;
    handleGroupMilestoneColumnChange: (
        groupId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
        applyToEveryFormula?: boolean,
    ) => void;
    handleToggleGroupFormatTypeOverride: (group: GroupWithChannels, formatTypeId: number, enabled: boolean) => void;
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    formatTypesOnWork?: { id: number; name: string }[];
    handleGroupActivation: (e: React.ChangeEvent<HTMLInputElement>, group: GroupWithChannels) => void;
    handleGroupUpdate: (newGroupData: unknown, groupChannels: IChannel[], formatTypeId?: number | null) => void;
    handleAlignGroupOnSync: (group: GroupWithChannels) => void;
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    from?: "templates" | "form";
    onShowDefaultConfigurationChange?: (channelId: number, showDefaultConfiguration: boolean) => void;
    onGroupShowDefaultConfigurationChange?: (group: GroupWithChannels, showDefaultConfiguration: boolean) => void;
}

const GroupsListSection = ({
    mode,
    activeGroups,
    inactiveGroups,
    activedChannels,
    chosenChannels,
    chosenGroups,
    activeGroupsIds,
    setChosenGroups,
    usesTemplate,
    onChange,
    handleChannelUpdates,
    handleToggleFormatTypeOverride,
    handleGroupCalculationBaseColumnChange,
    handleGroupMilestoneColumnChange,
    handleToggleGroupFormatTypeOverride,
    calculationBaseColumnLabels,
    formatTypesOnWork = [],
    handleGroupActivation,
    handleGroupUpdate,
    handleAlignGroupOnSync,
    useFormData,
    from = "form",
    onShowDefaultConfigurationChange,
    onGroupShowDefaultConfigurationChange,
}: GroupsListSectionProps) => {
    const [t] = useTranslation();
    const { setError, clearErrors } = useFormData;

    const renderGroup = (group: GroupWithChannels) => {
        const groupCurrentlyActive = activeGroupsIds?.some((item) => item === group.id);
        const groupActiveOnTitle =
            mode === "edit"
                ? chosenGroups?.some((item) => item.id === group.id)
                : activedChannels?.some((item) => group.channels.some((channel) => channel.id === item.channelId)) ??
                  false;

        const hasMilestones = group.channels.some((channel) => channel.hasMilestones === 1);
        const existingGroup = chosenGroups?.find((item) => group.id === item.id) ?? undefined;

        return (
            <GroupSection
                group={group}
                groupActiveOnTitle={groupActiveOnTitle}
                groupCurrentlyActive={groupCurrentlyActive}
                chosenChannels={chosenChannels}
                mode={mode}
                handleChannelUpdates={handleChannelUpdates}
                handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                handleGroupCalculationBaseColumnChange={handleGroupCalculationBaseColumnChange}
                handleGroupMilestoneColumnChange={handleGroupMilestoneColumnChange}
                handleToggleGroupFormatTypeOverride={handleToggleGroupFormatTypeOverride}
                calculationBaseColumnLabels={calculationBaseColumnLabels}
                formatTypesOnWork={formatTypesOnWork}
                handleGroupActivation={handleGroupActivation}
                handleGroupUpdate={handleGroupUpdate}
                handleAlignGroupOnSync={handleAlignGroupOnSync}
                hasMilestones={hasMilestones}
                groupOnEdit={existingGroup}
                setChosenGroups={setChosenGroups}
                chosenGroups={chosenGroups}
                key={`group-section-${group.id}`}
                onChange={onChange}
                usesTemplate={usesTemplate}
                useFormData={{ setError, clearErrors }}
                from={from}
                onShowDefaultConfigurationChange={onShowDefaultConfigurationChange}
                onGroupShowDefaultConfigurationChange={onGroupShowDefaultConfigurationChange}
            />
        );
    };

    return (
        <>
            {activeGroups.map((group) => renderGroup(group))}
            {mode === "edit" && inactiveGroups.length > 0 && (
                <InactiveItemsCollapse
                    label={t("ConnectTitle.InactiveGroupsCollapse", { count: inactiveGroups.length })}
                >
                    {inactiveGroups.map((group) => renderGroup(group))}
                </InactiveItemsCollapse>
            )}
        </>
    );
};

export default GroupsListSection;
